// The most a postpaid per-session cycle can cost: what a family approves for autopay at sign-up.
//
// Mirrors the backend (functions/src/helpers/billingCycle.ts maxBillingDays and
// allowanceWindow.ts maxSessionsPerCycle). The backend prices the real mandate; this is the same
// number for the wizard and checkout to show, so the two must change together.

/** The most calendar days one billing cycle can span ("every month" is up to 31 days). */
export function maxBillingDays(pricingPack: any): number {
  let value = 1;
  let unit = "month";
  if (pricingPack?.frequency === "CUSTOM") {
    const raw = pricingPack.billEveryUnit || pricingPack.durationUnit || "MONTHS";
    value = Number(pricingPack.billEveryValue ?? pricingPack.durationValue) || 1;
    unit = raw === "DAYS" ? "day" : raw === "WEEKS" ? "week" : "month";
  } else {
    unit = ({ DAILY: "day", WEEK: "week", MONTH: "month", YEAR: "year" } as Record<string, string>)[pricingPack?.frequency] || "month";
  }
  const n = Math.max(1, value);
  if (unit === "day") return n;
  if (unit === "week") return 7 * n;
  const months = unit === "year" ? 12 * n : n;
  const DAY = 24 * 60 * 60 * 1000;
  let most = 0;
  for (let year = 2024; year < 2028; year++) {
    for (let month = 0; month < 12; month++) {
      const days = Math.round((Date.UTC(year, month + months, 1) - Date.UTC(year, month, 1)) / DAY);
      if (days > most) most = days;
    }
  }
  return most;
}

/**
 * The most sessions one billing cycle can hold, whatever day it starts on.
 *
 * Unlimited: a session for every day of the longest cycle. With a limit per day/week/month: the
 * limit times every calendar window the cycle can touch, because each partial week or month at the
 * cycle's edges still allows the full limit (a 31-day month starting on a Sunday touches six weeks).
 */
export function maxSessionsPerCycle(pricingPack: any): number {
  const days = maxBillingDays(pricingPack);
  const raw = Number(pricingPack?.noOfClasses);
  const limit = pricingPack?.unlimitedClasses || !(raw > 0) ? null : raw;
  if (limit == null) return days;
  const unit = pricingPack?.sessionLimitUnit;
  if (!unit || unit === "CYCLE" || unit === "PACK") return limit;   // per billing period, or once for the whole pack
  const shortestWindow = ({ DAY: 1, WEEK: 7, MONTH: 28 } as Record<string, number>)[unit] || 1;
  return limit * (1 + Math.ceil((days - 1) / shortestWindow));
}
